import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatMenu, MatMenuModule } from '@angular/material/menu';
import { MatTableDataSource } from '@angular/material/table';

import { ColumnMap, RowData, TableSubMenuItem } from '../../../shared/interfaces/table.data';
import { AnalysisTableData, PluginsForAnalysisResponse } from '../../../interfaces/analysis';
import { TableComponent } from '../../../shared/table/table.component';
import { TableCellDirective } from '../../../shared/table/directives/table-cell.directive';
import { TitleCasePipe } from '../../../shared/pipes/title-case.pipe';
import { AnalysisContextActions, LoadingState } from '../analysis.types';

export { LoadingState };

export enum AnalysisColumns {
  Name = 'analysisName',
  AppName = 'appName',
  PanelName = 'panelName',
  GroupName = 'groupName',
  SampleName = 'sampleName',
  Status = 'status',
  CompletedOn = 'completedOn',
  User = 'user',
}

export const analysisTableMap: ColumnMap<AnalysisTableData>[] = [
  { key: AnalysisColumns.Name, label: $localize`Analysis Name`, accessor: (row) => row.analysisName },
  { key: AnalysisColumns.AppName, label: $localize`Application`, accessor: (row) => row.appName },
  { key: AnalysisColumns.PanelName, label: $localize`App Config Name`, accessor: (row) => row.panelName },
  { key: AnalysisColumns.SampleName, label: $localize`Sample Name`, accessor: (row) => row.sampleName },
  { key: AnalysisColumns.Status, label: $localize`Status`, accessor: (row) => row.status },
  { key: AnalysisColumns.CompletedOn, label: $localize`Completed On`, accessor: (row) => row.completedOn },
  { key: AnalysisColumns.GroupName, label: $localize`Project`, accessor: (row) => row.groupName },
  { key: AnalysisColumns.User, label: $localize`User`, accessor: (row) => row.user },
];

@Component({
  selector: 'app-analysis-table',
  standalone: true,
  imports: [CommonModule, TableComponent, TableCellDirective, TitleCasePipe, MatButtonModule, MatMenuModule],
  templateUrl: './analysis-table.component.html',
  styleUrl: './analysis-table.component.css',
})
export class AnalysisTableComponent implements AfterViewInit {
  readonly rowTypeToken!: AnalysisTableData;
  @Input() dataSource!: MatTableDataSource<RowData<AnalysisTableData>>;
  @Input() rowStatusOverrides: Map<number, string> = new Map();
  @Input() rowProgressMap: Map<number, number> = new Map();
  @Input() userRole: string = 'none';
  @Input() userCanManage: boolean = false;
  @Input() contextActionsMap: Map<number, AnalysisContextActions> = new Map();
  @Input() activeAnalysisId: number | null = null;
  @Input() isViewResultDisabled: (row: AnalysisTableData) => boolean = () => true;
  @Input() isDownloadResultDisabled: (row: AnalysisTableData) => boolean = () => true;
  @Input() isPluginMenuDisabled: (row: AnalysisTableData) => boolean = () => true;

  @Output() checkboxChanged = new EventEmitter<any>();
  @Output() rowMenuOpened = new EventEmitter<AnalysisTableData>();
  @Output() rowMenuClosed = new EventEmitter<void>();
  @Output() viewResultClicked = new EventEmitter<AnalysisTableData>();
  @Output() getInfoClicked = new EventEmitter<AnalysisTableData>();
  @Output() downloadRequested = new EventEmitter<{ type: 'results' | 'logs' | 'raw'; analysisId: number }>();
  @Output() pluginLaunched = new EventEmitter<{ pluginId: string; analysisId: number }>();
  @Output() editClicked = new EventEmitter<AnalysisTableData>();
  @Output() deleteClicked = new EventEmitter<AnalysisTableData>();

  @ViewChild(TableComponent) private innerTable?: TableComponent<AnalysisTableData>;
  @ViewChild('downloadMenu') private downloadMenuRef!: MatMenu;
  @ViewChild('pluginsMenu') private pluginsMenuRef!: MatMenu;

  readonly columnMap = analysisTableMap;
  protected readonly LoadingState = LoadingState;

  readonly isRowActiveFn = (row: RowData<AnalysisTableData>): boolean =>
    this.activeAnalysisId != null && row.data.analysisId === this.activeAnalysisId;

  activeRowId: number | null = null;
  activeRow: AnalysisTableData | null = null;
  subMenuItems: TableSubMenuItem[] = [];

  private readonly statusClassMap: Record<string, string> = {
    active: 'status-active',
    completed: 'status-uploaded',
    uploaded: 'status-uploaded',
    ready: 'status-active',
    error: 'status-error',
    inactive: 'status-inactive',
    processing: 'status-processing',
    analyzing: 'status-analyzing',
    created: 'status-uploaded',
    uploading: 'status-uploading',
  };

  private cdr = inject(ChangeDetectorRef);

  ngAfterViewInit(): void {
    this.subMenuItems = [
      {
        label: $localize`View Result`,
        getVisible: () => false, // TODO: being removed; hidden for now, logic left in place for later cleanup
        getDisabled: (row: AnalysisTableData) => this.isViewResultDisabled(row),
        onClick: (row: AnalysisTableData) => this.viewResultClicked.emit(row),
      },
      {
        label: $localize`Get Info`,
        getDisabled: () => false,
        onClick: (row: AnalysisTableData) => this.getInfoClicked.emit(row),
      },
      {
        label: $localize`Download Result`,
        menuRef: this.downloadMenuRef,
        getDisabled: (row: AnalysisTableData) => this.isDownloadResultDisabled(row),
      },
      {
        label: $localize`Plugins`,
        getVisible: () => false, // TODO: being removed; hidden for now, logic left in place for later cleanup
        menuRef: this.pluginsMenuRef,
        getDisabled: (row: AnalysisTableData) => this.isPluginMenuDisabled(row),
      },
      {
        label: $localize`Edit`,
        getDisabled: () => false,
        getVisible: () => this.userCanManage,
        onClick: (row: AnalysisTableData) => this.editClicked.emit(row),
      },
      {
        label: $localize`Delete`,
        getDisabled: () => false,
        getVisible: () => this.userCanManage,
        onClick: (row: AnalysisTableData) => this.deleteClicked.emit(row),
      },
    ];
    this.cdr.detectChanges();
  }

  onRowMenuOpenedInternal(row: AnalysisTableData): void {
    this.activeRowId = row.analysisId;
    this.activeRow = row;
    this.rowMenuOpened.emit(row);
  }

  getPluginsMenuState(): AnalysisContextActions['plugins'] | null {
    return this.activeRowId ? (this.contextActionsMap.get(this.activeRowId)?.plugins ?? null) : null;
  }

  getActiveRowStatus(): string {
    if (!this.activeRow) return '';
    return (this.rowStatusOverrides.get(this.activeRow.analysisId) || this.activeRow.status || '').toLowerCase();
  }

  getEffectiveStatus(row: RowData<AnalysisTableData>): string {
    const { analysisId, status } = row.data;
    return this.rowStatusOverrides.get(analysisId) || status || 'Unknown';
  }

  getEffectiveProgress(row: RowData<AnalysisTableData>): number {
    const { analysisId, percentComplete } = row.data;
    if (this.getEffectiveStatus(row).toLowerCase() === 'completed') return 100;
    return this.rowProgressMap.get(analysisId) ?? percentComplete ?? 0;
  }

  getStatusClass(row: RowData<AnalysisTableData>): string {
    return this.statusClassMap[this.getEffectiveStatus(row).toLowerCase()] || '';
  }

  resetSelections(): void {
    this.innerTable?.resetSelections();
  }
}
